import React, { useState } from 'react'
import type { Comment } from '../../types'
import { useAuth } from '../../hooks/useAuth'
import { CommentForm } from './CommentForm'

interface CommentItemProps {
  comment: Comment
  onLike: (id: string) => void
  onDelete: (id: string) => void
  onReply: (body: string, parentId: string) => Promise<boolean>
  depth?: number
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onLike,
  onDelete,
  onReply,
  depth = 0,
}) => {
  const { user, isLoggedIn } = useAuth()
  const [replying, setReplying] = useState(false)

  const isOwner = isLoggedIn && user?.userId === comment.authorId
  const canReply = depth === 0  // Only one level of nesting

  const handleReply = async (body: string): Promise<boolean> => {
    const ok = await onReply(body, comment.id)
    if (ok) setReplying(false)
    return ok
  }

  return (
    <>
      <style>{`
        .comment-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .comment-item + .comment-item {
          margin-top: 16px;
        }
        .comment-header {
          display: flex;
          align-items: baseline;
          gap: 10px;
          flex-wrap: wrap;
        }
        .comment-author {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--accent-teal);
        }
        .comment-date {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.68rem;
          color: var(--text-muted);
        }
        .comment-body {
          font-size: 0.9rem;
          line-height: 1.7;
          color: var(--text-primary);
          white-space: pre-wrap;
          word-break: break-word;
        }
        .comment-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .comment-action-btn {
          background: none;
          border: none;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.68rem;
          color: var(--text-muted);
          cursor: pointer;
          padding: 0;
          transition: color 0.15s ease;
        }
        .comment-action-btn:hover {
          color: var(--accent-blue);
        }
        .comment-action-btn.danger:hover {
          color: var(--accent-red);
        }
        .comment-like-count {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.68rem;
          color: var(--text-muted);
        }
        .comment-replies {
          margin-top: 12px;
          padding-left: 20px;
          border-left: 1px solid var(--border-color-solid);
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .comment-reply-form {
          margin-top: 8px;
        }
      `}</style>
      <div className="comment-item">
        <div className="comment-header">
          <span className="comment-author">{comment.authorName}</span>
          <span className="comment-date">{formatDate(comment.createdAt)}</span>
        </div>

        <p className="comment-body">{comment.body}</p>

        <div className="comment-actions">
          <button
            className="comment-action-btn"
            onClick={() => onLike(comment.id)}
            title="Like this comment"
          >
            ♥ {comment.likeCount > 0 ? comment.likeCount : ''}
          </button>

          {canReply && isLoggedIn && (
            <button
              className="comment-action-btn"
              onClick={() => setReplying((r) => !r)}
            >
              {replying ? '[cancel reply]' : '[reply]'}
            </button>
          )}

          {isOwner && (
            <button
              className="comment-action-btn danger"
              onClick={() => onDelete(comment.id)}
              title="Delete comment"
            >
              [delete]
            </button>
          )}
        </div>

        {replying && (
          <div className="comment-reply-form">
            <CommentForm
              onSubmit={handleReply}
              placeholder={`Reply to ${comment.authorName}...`}
              submitLabel="[post reply]"
              onCancel={() => setReplying(false)}
              autoFocus
            />
          </div>
        )}

        {comment.replies.length > 0 && (
          <div className="comment-replies">
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                onLike={onLike}
                onDelete={onDelete}
                onReply={onReply}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}
